/**
 * OpenRouter Service
 *
 * Handles AI chat interactions via OpenRouter API
 * Uses Gemini 2.0 Flash model (fixed, no model selection)
 *
 * Storage: API key persisted to Google Drive (credentials.json)
 * with sessionStorage as in-memory cache for current session
 */

import type {
  OpenRouterMessage,
  OpenRouterRequest,
  OpenRouterResponse,
  Item,
  AIMessage,
} from '@/types';
import { googleDrive } from './googleDrive';

const OPENROUTER_API = 'https://openrouter.ai/api/v1/chat/completions';
const OPENROUTER_AUTH_API = 'https://openrouter.ai/api/v1/auth/key';
const MODEL = 'google/gemini-3-flash-preview';

const STORAGE_KEY = 'openrouter_api_key';
const CREDENTIALS_FILE = 'credentials.json';

interface StoredCredentials {
  openRouterApiKey: string;
  version: 1;
}

class OpenRouterService {
  // ============================================
  // API KEY MANAGEMENT
  // ============================================

  /**
   * Get the stored API key
   * @returns API key or null if not set
   */
  getApiKey(): string | null {
    return sessionStorage.getItem(STORAGE_KEY);
  }

  /**
   * Store the API key
   * @param key - OpenRouter API key
   */
  setApiKey(key: string): void {
    sessionStorage.setItem(STORAGE_KEY, key);
  }

  /**
   * Clear the stored API key
   */
  clearApiKey(): void {
    sessionStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Check if an API key is configured
   */
  isConfigured(): boolean {
    return !!this.getApiKey();
  }

  // ============================================
  // DRIVE PERSISTENCE
  // ============================================

  /**
   * Load API key from Google Drive
   * @returns True if key was found and loaded
   */
  async loadFromDrive(): Promise<boolean> {
    try {
      const credentials = await googleDrive.readJson<StoredCredentials | null>(
        CREDENTIALS_FILE,
        null
      );
      if (credentials?.openRouterApiKey) {
        this.setApiKey(credentials.openRouterApiKey);
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to load credentials from Drive:', error);
      return false;
    }
  }

  /**
   * Save API key to Google Drive for persistence
   */
  async saveToDrive(): Promise<void> {
    const key = this.getApiKey();
    if (!key) return;

    const credentials: StoredCredentials = {
      openRouterApiKey: key,
      version: 1,
    };
    await googleDrive.writeJsonImmediate(CREDENTIALS_FILE, credentials);
  }

  /**
   * Clear credentials from Google Drive
   */
  async clearFromDrive(): Promise<void> {
    try {
      // Write empty credentials to effectively clear
      await googleDrive.writeJsonImmediate(CREDENTIALS_FILE, { version: 1 });
    } catch (error) {
      console.warn('Failed to clear credentials from Drive:', error);
    }
  }

  /**
   * Validate an API key with OpenRouter
   * @param key - API key to validate
   * @returns True if valid
   */
  async validateKey(key: string): Promise<boolean> {
    try {
      const response = await fetch(OPENROUTER_AUTH_API, {
        headers: { Authorization: `Bearer ${key}` },
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // ============================================
  // CHAT API
  // ============================================

  /**
   * Send a chat request to OpenRouter
   *
   * @param messages - Array of messages (system, user, assistant)
   * @returns Assistant's response text
   * @throws Error if API key not configured or request fails
   *
   * @example
   * const response = await openRouter.chat([
   *   { role: 'system', content: 'You are a helpful assistant.' },
   *   { role: 'user', content: 'Hello!' }
   * ]);
   */
  async chat(
    messages: OpenRouterMessage[],
    options?: { maxTokens?: number; temperature?: number }
  ): Promise<string> {
    const apiKey = this.getApiKey();
    if (!apiKey) {
      throw new Error('API key not configured. Please enter your OpenRouter API key in settings.');
    }

    const request: OpenRouterRequest = {
      model: MODEL,
      messages,
      temperature: options?.temperature ?? 0.7,
      max_tokens: options?.maxTokens ?? 2000,
    };

    const response = await fetch(OPENROUTER_API, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': window.location.origin,
        'X-Title': 'Personal Insights',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as {
        error?: { message?: string };
      };
      const errorMessage =
        errorData.error?.message ?? `Request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }

    const data: OpenRouterResponse = await response.json();
    const content = data.choices[0]?.message?.content;

    if (!content) {
      throw new Error('No response content received from AI');
    }

    return content;
  }

  /**
   * Stream a chat response (for real-time display)
   * Currently not implemented - using non-streaming for simplicity
   */
  async *chatStream(messages: OpenRouterMessage[]): AsyncGenerator<string, void, unknown> {
    // For now, just yield the full response
    // Streaming can be added later if needed
    const response = await this.chat(messages);
    yield response;
  }

  // ============================================
  // PER-ITEM CHAT
  // ============================================

  /**
   * Chat about a specific item with context
   *
   * @param item - The item to chat about
   * @param userMessage - User's message
   * @param chatHistory - Previous chat messages for context
   * @returns Assistant's response text
   */
  async chatAboutItem(item: Item, userMessage: string, chatHistory: AIMessage[]): Promise<string> {
    const systemPrompt = this.buildItemContextPrompt(item);

    const messages: OpenRouterMessage[] = [
      { role: 'system', content: systemPrompt },
      // Include recent chat history for context (limit to last 10)
      ...chatHistory.slice(-10).map((m) => ({
        role: m.role,
        content: m.content,
      })),
      { role: 'user', content: userMessage },
    ];

    return this.chat(messages);
  }

  /**
   * Build a context prompt based on item type
   */
  private buildItemContextPrompt(item: Item): string {
    const baseInstructions =
      'Be conversational, helpful, and concise. Focus on helping the user understand, analyze, and learn.';

    switch (item.type) {
      case 'link':
        return `You are helping the user learn about a saved link.

Title: ${item.title}
URL: ${item.url}
Description: ${item.description || 'None'}
Site: ${item.siteName || 'Unknown'}
Personal Notes: ${item.personalNotes || 'None'}

${baseInstructions}`;

      case 'podcast':
        return `You are helping the user learn about a saved podcast.

Title: ${item.title}
URL: ${item.url}
Podcast: ${item.podcastName || 'Unknown'}
Description: ${item.description || 'None'}
Personal Notes: ${item.personalNotes || 'None'}

${baseInstructions}`;

      case 'note':
        return `You are helping the user explore their note.

Title: ${item.title}
Content: ${item.plainTextPreview || item.description || 'None'}

Help expand on, analyze, or develop this note. ${baseInstructions}`;

      case 'idea':
        return `You are helping the user explore their idea.

Title: ${item.title}
Content: ${item.plainTextPreview || item.description || 'None'}

Help expand on, analyze, or develop this idea. ${baseInstructions}`;

      case 'file':
        return `You are helping the user with a saved file.

Title: ${item.title}
Filename: ${item.originalFileName}
Type: ${item.mimeType}
Description: ${item.description || 'None'}
Personal Notes: ${item.personalNotes || 'None'}

${baseInstructions}`;

      case 'task':
        return `You are helping the user with a task.

Task: ${item.title}
Status: ${item.isCompleted ? 'Completed' : 'In Progress'}
Description: ${item.description || 'None'}

Help break down, plan, or discuss this task. ${baseInstructions}`;
    }
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Get the model being used
   */
  getModel(): string {
    return MODEL;
  }

  /**
   * Get a display-friendly model name
   */
  getModelDisplayName(): string {
    return 'Gemini 3 Flash';
  }

  /**
   * Estimate token count for a string (rough approximation)
   * @param text - Text to estimate
   * @returns Approximate token count
   */
  estimateTokens(text: string): number {
    // Rough estimate: ~4 characters per token for English
    return Math.ceil(text.length / 4);
  }

  /**
   * Check if messages are within token limits
   * @param messages - Messages to check
   * @param maxTokens - Maximum tokens (default 100000 for Gemini)
   * @returns True if within limits
   */
  isWithinTokenLimit(messages: OpenRouterMessage[], maxTokens = 100000): boolean {
    const totalText = messages.map((m) => m.content).join(' ');
    const estimatedTokens = this.estimateTokens(totalText);
    return estimatedTokens < maxTokens * 0.9; // 90% threshold for safety
  }
}

// Export singleton instance
export const openRouter = new OpenRouterService();
